// v-enter-submit: keyboard submit behavior for <el-form> / <form>.
//
// Usage:
//   <el-form v-enter-submit="handleCreate">
//   <el-form v-enter-submit="() => handleSubmit('form')">
//   <el-form v-enter-submit="{ handler: handleSubmit, disabled: !canSubmit }">
//
// Behavior:
//   - Always blocks the native form submit (which would reload the page).
//   - Enter in a text input: if a required field is still empty, focus it
//     (after the current one first; forms without required rules treat every field
//     as required), otherwise run the handler.
//   - Enter in a textarea inserts a newline; Ctrl/Cmd+Enter runs the handler.
//   - Keys already handled by a component (e.g. el-select picking an option calls
//     preventDefault) and IME composition (Vietnamese Telex/VNI) are ignored.
//   - Held-down Enter (key repeat) is ignored, and while a handler's returned
//     promise is pending further Enter presses are ignored.

const SKIP_INPUT_TYPES = ['hidden', 'checkbox', 'radio', 'file', 'button', 'submit', 'reset', 'range', 'color'];

function isTextField(node) {
  if (node.tagName === 'TEXTAREA') return true;
  if (node.tagName !== 'INPUT') return false;
  return !SKIP_INPUT_TYPES.includes((node.type || 'text').toLowerCase());
}

function isNavigable(node) {
  return (
    isTextField(node) &&
    !node.disabled &&
    // Non-filterable el-select / picker inputs are readonly; they are not typed into.
    !node.readOnly &&
    node.getClientRects().length > 0
  );
}

// Pick the field Enter should move to instead of submitting: the first empty
// required field after the current one, else the first empty one before it.
// Forms without any required rule treat every field as required.
function findMissingField(el, target) {
  const fields = Array.from(el.querySelectorAll('input, textarea')).filter(isNavigable);
  const required = fields.filter((field) => field.closest('.el-form-item.is-required'));
  const pool = required.length ? required : fields;
  const position = fields.indexOf(target);
  const empty = pool.filter((field) => field !== target && field.value === '');
  return empty.find((field) => fields.indexOf(field) > position) || empty[0] || null;
}

function normalize(value) {
  if (typeof value === 'function') return { handler: value, disabled: false };
  return { handler: value && value.handler, disabled: !!(value && value.disabled) };
}

function runHandler(el) {
  const state = el.__enterSubmit__;
  const { handler, disabled } = state.options;
  if (disabled || state.pending || typeof handler !== 'function') return;

  const result = handler();
  if (result && typeof result.then === 'function') {
    state.pending = true;
    // Not .finally(): it would re-reject and surface as an unhandled rejection.
    result.then(
      () => {
        state.pending = false;
      },
      (error) => {
        state.pending = false;
        console.error(error);
      },
    );
  }
}

function onKeydown(event) {
  const el = event.currentTarget;
  const target = event.target;

  if (event.key !== 'Enter') return;
  if (event.isComposing || event.keyCode === 229) return;
  if (event.defaultPrevented) return;
  if (!isTextField(target)) return;

  const withModifier = event.ctrlKey || event.metaKey;
  if (target.tagName === 'TEXTAREA' && !withModifier) return;

  event.preventDefault();
  if (event.repeat) return;

  if (!withModifier) {
    const missing = findMissingField(el, target);
    if (missing) {
      missing.focus();
      return;
    }
  }

  runHandler(el);
}

function onSubmit(event) {
  event.preventDefault();
}

export default {
  bind(el, binding) {
    el.__enterSubmit__ = { options: normalize(binding.value), pending: false };
    el.addEventListener('keydown', onKeydown);
    el.addEventListener('submit', onSubmit);
  },
  update(el, binding) {
    el.__enterSubmit__.options = normalize(binding.value);
  },
  unbind(el) {
    el.removeEventListener('keydown', onKeydown);
    el.removeEventListener('submit', onSubmit);
    delete el.__enterSubmit__;
  },
};
